import { useUpdateQuestionnaire } from "@app/screens/ExternalQuestionnaires/data";
import { useQuestionnaireDetails } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/data";
import { useOptimistic } from "@app/shared/utils/optimisticUpdates";
import { useEntities } from "@app/store/entityStore";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import { useMemo, type FC } from "react";
import { useEditOrDeleteProjectModalTranslations } from "./i18n";
import { Textfield } from "@design-system/Inputs/Textfield";

export const EditProjectModalContent: FC<{
    projectId: string;
    onDelete: () => void;
    onEdited: () => void;
}> = ({ projectId, onDelete, onEdited }) => {
    const { t, keys } = useEditOrDeleteProjectModalTranslations();
    const { questionnaire: project } = useQuestionnaireDetails(projectId, {
        enabled: true,
    });
    const { update } = useUpdateQuestionnaire();
    const { entities } = useEntities();

    const [selectedEntityId, setSelectedEntityId] = useOptimistic<
        string | undefined | null
    >(project?.entityId);
    const [name, setName] = useOptimistic<string | undefined | null>(
        project?.name,
    );
    const selectedEntity = useMemo(() => {
        return entities?.find((entity) => entity.id === selectedEntityId);
    }, [entities, selectedEntityId]);

    const handleEntityChange = ([value]: string[]) => {
        if (!value || value === project?.entityId) return;
        setSelectedEntityId(value);
    };

    const handleConfirm = () => {
        update(
            {
                id: projectId,
                input: { name, entityId: selectedEntityId },
            },
            {
                onSettled: () => {
                    onEdited();
                },
            },
        );
    };

    const handleSetName = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    };
    return (
        <Modal.Content className="max-w-[600px]">
            <Modal.Header withClose>
                <Modal.Title>{t(keys.edit_project_modal_title)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FlexCol gap="6">
                    <FlexCol gap="2">
                        <Textfield
                            placeholder={t(
                                keys.edit_project_modal_name_placeholder,
                            )}
                            value={name ?? ""}
                            onChange={handleSetName}
                        />
                        <Text variant="body-bold">
                            {t(keys.edit_project_modal_entity_label)}
                        </Text>
                        <Dropdown
                            value={[selectedEntityId ?? ""]}
                            onValueChange={handleEntityChange}
                            selectType="single"
                        >
                            <Dropdown.Trigger asChild className="w-full">
                                <Button
                                    variant="outlined"
                                    className="justify-between h-9 shadow-none border-tertiary max-w-[200px]"
                                    disabled={!entities?.length}
                                >
                                    <FlexRow gap="2">
                                        <Icon name="building" />
                                        <Text variant="body-bold">
                                            {selectedEntity?.name}
                                        </Text>
                                    </FlexRow>
                                    <Icon name="chevronDown" />
                                </Button>
                            </Dropdown.Trigger>
                            <Dropdown.Content align="start">
                                {entities?.map((e) => (
                                    <Dropdown.SelectableItem
                                        value={e.id}
                                        key={e.id}
                                    >
                                        {e.name}
                                        <Dropdown.SelectedIndicator />
                                    </Dropdown.SelectableItem>
                                ))}
                            </Dropdown.Content>
                        </Dropdown>
                    </FlexCol>

                    <FlexRow gap="2">
                        <Button
                            variant="text"
                            className="w-full text-error-500 max-w-fit"
                            size="md"
                            onClick={onDelete}
                        >
                            <Button.Icon name="trash" />
                        </Button>
                        <FlexCol>
                            <Text variant="body-bold">
                                {t(
                                    keys.edit_project_modal_delete_project_section_title,
                                )}
                            </Text>
                            <Text variant="body" color="secondary">
                                {t(
                                    keys.edit_project_modal_delete_project_section_description,
                                )}
                            </Text>
                        </FlexCol>
                    </FlexRow>
                </FlexCol>
            </Modal.Body>
            <Modal.Footer>
                <Modal.CancelCTA>
                    {t(keys.edit_project_modal_cancel_button_label)}
                </Modal.CancelCTA>
                <Modal.ConfirmCTA preventClose onClick={handleConfirm}>
                    {t(keys.edit_project_modal_confirm_button_label)}
                </Modal.ConfirmCTA>
            </Modal.Footer>
        </Modal.Content>
    );
};
